import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { cn } from '@/lib/utils'

/**
 * Labelled Radix/shadcn Select for forms. When nested under another layer that uses
 * `disableOutsidePointerEvents` (e.g. Dialog), Radix leaves the dialog subtree with
 * inherited `pointer-events: none` while the select is open. The trigger and this
 * wrapper use `pointer-events-auto` so outside clicks and trigger toggles still work.
 */
export function FormSelect({
    id,
    label,
    placeholder,
    options,
    value,
    onValueChange,
    disabled,
    required,
    triggerClassName,
    contentClassName,
    /** Forwarded to `SelectContent` (e.g. `position`, `container`, collision props). */
    contentProps,
    className,
}) {
    const valueUnset = (v) => !v || v === 'select_template';
    const resolved = valueUnset(value) ? '' : String(value);

    return (
        <div className={cn('relative flex flex-col text-sm gap-1 w-full pointer-events-auto', className)}>
            {label ? (
                <label htmlFor={id} className="font-semibold font-SuisseIntlLight text-[14px]">
                    {label}
                </label>
            ) : null}
            <div className="relative w-full">
                {required ? (
                    <input
                        tabIndex={-1}
                        required
                        value={resolved}
                        onChange={() => {}}
                        className="absolute inset-0 z-10 h-[42px] w-full opacity-0 pointer-events-none"
                    />
                ) : null}
                <Select value={resolved} onValueChange={onValueChange} disabled={disabled} required={required}>
                    <SelectTrigger
                        id={id}
                        className={cn(
                            'h-[42px] w-full rounded-[10px] border border-[#E2E4E9] bg-white px-3 text-[14px] font-SuisseIntlLight',
                            'text-[#525866] inputFieldShadow hover:border-[#CED0D5] focus:ring-0 focus:ring-offset-0',
                            'data-[placeholder]:text-[#868C98] pointer-events-auto',
                            triggerClassName
                        )}
                    >
                        <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent
                        position="popper"
                        className={cn('font-SuisseIntlLight', contentClassName)}
                        onCloseAutoFocus={(e) => e.preventDefault()}
                        {...contentProps}
                    >
                        {options?.map((opt) => (
                            <SelectItem
                                key={String(opt.value)}
                                value={String(opt.value)}
                                className="font-SuisseIntlLight text-[14px]"
                            >
                                {opt.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
}
